<script lang="ts" context="module">
  export const selectedGift = writable<{
    handle: string;
    variantId?: string;
    sellingPlanId?: string;
  }>({
    handle: '',
    variantId: undefined,
    sellingPlanId: undefined
  });
</script>

<script lang="ts">
  import { writable } from 'svelte/store';
  import Skeleton from '$components/ui/skeleton.svelte';
  import { CART_DRAWER_ID, FREE_GIFT } from '$lib/constants';
  import { lang } from '$lib/lang';
  import { shopify } from '$lib/shopify';
  import { t } from '$lib/i18n';
  import { freeGiftCampaign, getCampaignColors } from '$lib/stores/campaigns';
  import { cn } from '$lib/utils';
  import { createQueries } from '@tanstack/svelte-query';
  import type { LanguageCode } from '$gql/storefront/schemas';
  import type { ProductCardFragment } from '$gql/storefront/operations';
  import ProductCard from '$components/product-card/product-card.svelte';
  import { parseCountryAsCode, optimalUSCountry } from '$lib/country';
  import Button from '$components/ui/button.svelte';
  import { cart } from '$lib/cart';
  import { drawer } from '$lib/stores/drawer';
  import { trimVariantId } from '$lib/utils/shopify/productsId';
  import ChangeSize from './change-size.svelte';
  import { toaster } from '$lib/toaster';
  import { updateGift } from '$lib/cart/actions';

  export let expectedResults = 1;
  let productsAvailable: ProductCardFragment[] = [];
  let handles: string[] = [];
  let open = false;
  let updating = false;

  $: if ($freeGiftCampaign?.campaign_free_gifts) {
    handles = $freeGiftCampaign?.campaign_free_gifts
      ?.filter((p) => typeof p !== 'string')
      .map((p) => p.slug) as string[];

    expectedResults = handles.length;
  }

  $: queries = createQueries(
    handles.map((handle) => ({
      staleTime: 5 * 60 * 1000,
      queryKey: ['productCard', handle],
      queryFn: async () => {
        return await $shopify.getProductCard({
          handle,
          lang: $lang.toUpperCase() as LanguageCode,
          country: parseCountryAsCode($optimalUSCountry)
        });
      }
    }))
  );

  $: products = [...($queries || []).map((query) => query.data?.product)].filter(
    (p) => p != null
  ) as ProductCardFragment[];

  $: freeProduct = $cart?.lines.nodes.find((line) =>
    (line.attributes || [])?.some((item) => item.key === FREE_GIFT)
  );
  $: {
    const allProducts = products.filter((item) => item.availableForSale);
    if (!updating) {
      productsAvailable = allProducts.reduce<ProductCardFragment[]>((acc, item) => {
        item.handle === freeProduct?.merchandise?.product?.handle
          ? acc.unshift(item)
          : acc.push(item);
        return acc;
      }, []);
    }
  }
  $: product = products.find((item) => item.handle === $selectedGift?.handle);
  $: loading = $queries.some((q) => q.isLoading);

  $: if (productsAvailable && products.length > 0) {
    const cartItem = $cart?.lines.nodes.find((line) =>
      (line.attributes || [])?.some((item) => item.key === FREE_GIFT)
    );

    if (cartItem) {
      selectedGift.set({
        handle: cartItem?.merchandise?.product.handle,
        variantId: trimVariantId(cartItem?.merchandise?.id as string)
      });
    }
  }

  $: hasOnlyDefaultVariant =
    product?.options?.length === 1 && product?.options[0].values.length === 1;

  $: showConfirmButton =
    trimVariantId(freeProduct?.merchandise?.id || '') !== $selectedGift.variantId;

  $: freeGiftCampaignColors = $freeGiftCampaign?.campaign_color
    ? getCampaignColors($freeGiftCampaign?.campaign_color)
    : null;

  const handleGiftChange = () => {
    // update the gifts only if a new item is selected
    updating = true;
    $updateGift()
      .then(() => {
        drawer.open(CART_DRAWER_ID, {
          meta: undefined
        });
      })
      .finally(() => {
        toaster({
          type: 'success',
          title: `${$t('cart.gift-change-success')}`
        });
        updating = false;
      });
  };
</script>

<div class="p-2">
  <div
    class="grid grid-cols-2 gap-2 pb-[70px]"
    style:--freeGiftAltTextColor={freeGiftCampaignColors?.altTextColor}
  >
    {#if products.length > 0 && !loading}
      {#each productsAvailable as product}
        {#if product}
          <ProductCard
            variant="gift-card"
            {product}
            class={cn('w-full rounded-sm')}
            on:handleSelectGift={() => (open = true)}
          />
        {/if}
      {/each}
    {:else}
      {#each [...Array(expectedResults)] as _}
        <Skeleton class={cn('aspect-[228/344] rounded-md')} />
      {/each}
    {/if}
  </div>

  {#if showConfirmButton}
    <div class="fixed lg:absolute bottom-0 left-0 z-40 p-3 w-full bg-background">
      <Button class={cn('w-full')} on:click={() => handleGiftChange()} loading={updating}>
        {$t('cart.confirm-selection')}
      </Button>
    </div>
  {/if}

  {#if product && $selectedGift?.variantId && !hasOnlyDefaultVariant && open}
    <ChangeSize {product} on:close={() => (open = false)} on:save={() => (open = false)} />
  {/if}
</div>
